import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Container,
    Title,
    Paper,
    Text,
    Badge,
    Stack,
    ScrollArea,
    Alert,
    Button,
    Divider,
    Box,
} from '@mantine/core';
import { IconAlertCircle, IconEye, IconCash } from '@tabler/icons-react';
import { collection, getDocs, query } from 'firebase/firestore';
import { db } from '../../firebase/FirebaseCredential';
import { useAppSelector } from '../../hooks/hooks';
import { RootState } from '../../store/store';
import BenchBreadcrumbs from '../UIComponent/Components/BenchBreadcrumbs';
import BenchLoader from '../UIComponent/BenchLoader/BenchLoader';
import { BenchTable } from '../pages/UI';
import { ROUTES } from '../../routes/routesPaths';
import BenchSettingsBreadcrumbs from '../UIComponent/Components/BenchSettingsBreadcrumbs';
import BenchPaper from '../pages/UI/BenchPaper';
import BenchTopHeader from '../UIComponent/Components/BenchTopHeader';

interface PayrollPeriod {
    id: string;
    company_id: string;
    created_at: any;
    end_date: string;
    message: string;
    month: string;
    pay_cycle_frequency: string;
    pay_period: string;
    payroll_process_date: string;
    process_date: any;
    start_date: string;
    total_employees: number;
    total_payout: number;
    year: number;
}

export default function PayrollMainPage() {
    const navigate = useNavigate();
    const companyId = useAppSelector((state: RootState) => state.session.company_id);

    const [payrollPeriods, setPayrollPeriods] = useState<PayrollPeriod[]>([]);
    const [pageLoading, setPageLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    const breadcrumbLinks = [{ label: 'Payroll' }];

    useEffect(() => {
        const fetchPayrollPeriods = async () => {
            if (!companyId) {
                setError('Company ID not found');
                setPageLoading(false);
                return;
            }

            try {
                setPageLoading(true);
                const payrollRef = collection(db, 'companyprofile', companyId, 'payroll');
                const q = query(payrollRef);
                const querySnapshot = await getDocs(q);

                const periods: PayrollPeriod[] = [];
                querySnapshot.forEach((doc) => {
                    periods.push({ id: doc.id, ...doc.data() } as PayrollPeriod);
                });

                periods.sort((a, b) => {
                    const dateA = a.process_date?.toDate?.() || new Date(0);
                    const dateB = b.process_date?.toDate?.() || new Date(0);
                    return dateB.getTime() - dateA.getTime();
                });

                setPayrollPeriods(periods);
                setError(null);
            } catch (err) {
                console.error('Error fetching payroll periods:', err);
                setError('Failed to load payroll data');
            } finally {
                setPageLoading(false);
            }
        };

        fetchPayrollPeriods();
    }, [companyId]);

    const formatCurrency = (amount: number) => `₹${amount.toLocaleString('en-IN')}`;

    const formatDate = (timestamp: any) => {
        if (!timestamp) return 'N/A';
        const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
        return date.toLocaleDateString('en-IN', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
        });
    };

    if (error) {
        return (
            <Container size="xl" py="xl">
                <Alert icon={<IconAlertCircle size={16} />} title="Error" color="red">
                    {error}
                </Alert>
            </Container>
        );
    }

    return (
        <div className="page">
            <Container size="lg" py="md">
                 <Box
            sx={(theme) => ({
              position: "sticky",
              top: 110, // Avoid overlapping the fixed navbar
              zIndex: 100,
              backgroundColor: "#FAFAFA",
              paddingTop: theme.spacing.sm,
              //paddingBottom: theme.spacing.xs,
              marginTop: `-${theme.spacing.sm}`,
            })}
          >
                <BenchBreadcrumbs links={breadcrumbLinks} />
                <Divider my="sm" /> </Box> <Stack spacing="lg" mt="sm">

                    <BenchTopHeader
                        icon={<IconCash />}
                        title="Payroll Dashboard"
                        description="Manage employee payroll, track payouts, and view historical payroll records."
                        iconColor="#ee374d"
                    />
                    {/* <Divider my="sm" /> */}

                    {payrollPeriods.length === 0 ? (
                        <BenchPaper title="Payroll Periods">
                            <Text align="center" color="dimmed" py="xl">
                                No payroll periods found
                            </Text>
                        </BenchPaper>
                    ) : (
                        <ScrollArea style={{ marginBottom: '2rem' }}>
                            <BenchTable>
                                <thead>
                                    <tr>
                                        <th>Pay Period</th>
                                        <th>Month / Year</th>
                                        <th>Frequency</th>
                                        <th>Employees</th>
                                        <th>Total Payout</th>
                                        <th>Process Date</th>
                                        <th>Status</th>
                                        <th>Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {payrollPeriods.map((period) => (
                                        <tr key={period.id}>
                                            <td><Text weight={700}>{period.pay_period}</Text></td>
                                            <td><Text>{period.month} {period.year}</Text></td>
                                            <td>
                                                <Badge color="blue" variant="light">
                                                    {period.pay_cycle_frequency}
                                                </Badge>
                                            </td>
                                            <td>{period.total_employees}</td>
                                            <td><Text weight={700}>{formatCurrency(period.total_payout)}</Text></td>
                                            <td>{formatDate(period.process_date)}</td>
                                            <td>
                                                <Badge color="green" variant="light">
                                                    Processed
                                                </Badge>
                                            </td>
                                            <td>
                                                <Button
                                                    size="xs"
                                                    variant="light"
                                                    leftIcon={<IconEye size={16} />}
                                                    onClick={() =>
                                                        navigate(
                                                            ROUTES.PAYROLL_EMPLOYEES_PAGE.replace(':payrollId', period.id)
                                                        )
                                                    }
                                                >
                                                    View Details
                                                </Button>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </BenchTable>
                        </ScrollArea>
                    )}
                </Stack>
            </Container>

            <BenchLoader isLoading={pageLoading} />
        </div>
    );
}
